import React, {useState} from "react";
import { ColumnLayout, ExpandableSection } from "@amzn/awsui-components-react";
import { v4 as uuid } from "uuid";
import PropTypes from "prop-types";
import {
  getSectionConstant,
  SECTION,
  SECTION_CONSTANT_TYPE,
} from "../../../../shared/util/constants/section";
import { PROGRAM_TYPE } from "../../../../shared/util/constants/programType";
import { ISV_WORKLOAD_MIGRATION_PROGRAM_NAME } from "../../../../shared/util/constants/subProgram";
import { uiToApiMap } from "../../../../shared/util/adapter/toUi/adapter";
import { filterFields, schema } from "../../../config/schema";
import { DefaultSectionHeader } from "./SectionHeader/DefaultSectionHeader";
import { createValueField } from "../../../../shared/components/Review/CreateValueField";
import { ValueFactory } from "./ValueFactory";
import { isFeatureEnabled } from "shared/util/services/features/FeatureFlagsService";
import { FEATURE_FLAG_ENUMS } from "shared/util/constants/featureFlagValues";

export const DefaultReviewSectionContainer = ({
  fundingData,
  step,
  template,
}) => {
  const [isSCAEnabled, setIsSCAEnabled] = React.useState(false);
  const [isSimplifiedCreditEnabled, setIsSimplifiedCreditEnabled] = React.useState(false);
  const [isMPOPPEnabled, setIsMPOPPEnabled] = React.useState(false);
  const [isMPOPPValidationsEnabled, setIsMPOPPValidationsEnabled] = React.useState(false);
  const [isSCAP1ProgramsEnabled, setIsSCAP1ProgramsEnabled] = useState(false);

  React.useEffect(async () => {
    setIsSCAEnabled(
      await isFeatureEnabled(FEATURE_FLAG_ENUMS.SCA_FUNDING_ENABLED)
    );
    setIsSimplifiedCreditEnabled(
      await isFeatureEnabled(FEATURE_FLAG_ENUMS.SIMPLIFY_ISV_CREDIT_REQUEST_ENABLED)
    );
    setIsMPOPPEnabled(
      await isFeatureEnabled(FEATURE_FLAG_ENUMS.MPOPP_ENABLED)
    );
    setIsMPOPPValidationsEnabled(
      await isFeatureEnabled(FEATURE_FLAG_ENUMS.MPOPP_VALIDATIONS_ENABLED)
    );
    setIsSCAP1ProgramsEnabled(
      await isFeatureEnabled(FEATURE_FLAG_ENUMS.ENABLE_SCA_P1_PROGRAMS)
    );
  }, []);
  const fields = [];

  for (let sectionTitle of template[step].sections) {
    const sectionName = getSectionConstant({
      type: SECTION_CONSTANT_TYPE.NAME,
      value: sectionTitle,
    });

    const apiKey = uiToApiMap[sectionName];
    const filteredFields = filterFields({
      section: schema[sectionTitle],
      program: fundingData.program,
    });

    for (let field of filteredFields) {
      // Boolean on whether or not to hide valuePerCode and numberOfCodes fields for ISV WMP
      const activateSimplifyCredit = isSimplifiedCreditEnabled && 
        fundingData.program === PROGRAM_TYPE.MISC && 
        fundingData.subProgram && 
        fundingData.subProgram === ISV_WORKLOAD_MIGRATION_PROGRAM_NAME &&
        ["valuePerCode", "numberOfCodes"].includes(field.name);

      // Check if finalCreditAmount and agreementContractValue fields should be hidden
      const hideFinalCreditAmount = (field.name === "finalCreditAmount" || field.name === "agreementContractValue") &&
        (!isMPOPPEnabled || 
         fundingData.program !== PROGRAM_TYPE.PIF || 
         !fundingData.subProgram || 
         !['MPOPP Activate', 'MPOPP Grow'].includes(fundingData.subProgram));

      // Check if deployedOnAws field should be hidden (using MPOPP_VALIDATIONS_ENABLED flag)
      const hideDeployedOnAws = field.name === "deployedOnAws" &&
        (!isMPOPPValidationsEnabled || 
         fundingData.program !== PROGRAM_TYPE.PIF || 
         !fundingData.subProgram || 
         !['MPOPP Activate', 'MPOPP Grow'].includes(fundingData.subProgram));

      // Hide finalCreditAmount if deployedOnAws is not true (only when MPOPP_VALIDATIONS_ENABLED is true)
      const hideFinalCreditAmountIfNotDeployed = field.name === "finalCreditAmount" && 
        isMPOPPValidationsEnabled && 
        fundingData.program === PROGRAM_TYPE.PIF && 
        fundingData.subProgram && 
        ['MPOPP Activate', 'MPOPP Grow'].includes(fundingData.subProgram) && 
        !fundingData.creditRequest?.deployedOnAws;

      if ((
        !isSCAEnabled &&
        (field.name === "strategicCollaborationAgreementCredit" ||
          field.name === "strategicCollaborationAgreement")
        ) 
        || activateSimplifyCredit || hideFinalCreditAmount || hideDeployedOnAws || hideFinalCreditAmountIfNotDeployed
      ) {
        // sca link and checkbox disable
        continue;
      }
      if (
        !isSCAP1ProgramsEnabled &&
        (field.name === "contractCentralIdCredit" ||
          field.name === "phoenixCreditIdCredit")
      ) {
        continue;
      }
      const escCurrencyFields = [
        "annualRunRateForMisc",
        "valuePerCode",
        "requestedCreditAmount",
        "requestedCreditAmountEditable",
        "approvedCreditAmount",
        "issuedCreditAmount",
        "finalCreditAmount",
      ];
      const resolvedField =
        escCurrencyFields.includes(field.name) &&
        fundingData.isEuropeanSovereignCloud
          ? { ...field, label: field.label.replace("(USD)", "(EUR)") }
          : field;
      fields.push(
        createValueField({
          field: resolvedField,
          value: ValueFactory({
            apiKey,
            sectionTitle,
            name: field.name,
            data: fundingData,
            field,
          }),
        })
      );
    }
  }

  // Add Cloud Region field for ESC fund requests (only in Fund Request Information section)
  if (
    fundingData.isEuropeanSovereignCloud !== undefined &&
    template[step].sections.includes(SECTION.FUND_REQUEST_INFORMATION)
  ) {
    fields.push(
      createValueField({
        field: { name: "cloudRegion", label: "Cloud Region" },
        value: fundingData.isEuropeanSovereignCloud
          ? "European Sovereign Cloud (ESC)"
          : "Commercial",
      })
    );
  }

  return (
    <React.Fragment key={uuid()}>
      <ExpandableSection
        variant="container"
        defaultExpanded={true}
        header={
          <DefaultSectionHeader
            title={template[step].title}
            name={template[step].name}
            template={template}
            stage={fundingData.stage}
            status={fundingData.status}
            program={fundingData.program}
            isAutoApproved={
              fundingData["approval"] &&
              fundingData["approval"]["eligibleForPsaAutoApproval"]
            }
          />
        }
      >
        <ColumnLayout columns={4} variant="text-grid">
          {fields}
        </ColumnLayout>
      </ExpandableSection>
      <br />
    </React.Fragment>
  );
};

DefaultReviewSectionContainer.propTypes = {
  fundingData: PropTypes.object,
  step: PropTypes.array,
  template: PropTypes.object,
};
